import axios from "axios";
import { setFooterLinks } from "../action/appConfiguration";

// Default footer links used as initial data and fallback
export const DEFAULT_FOOTER_LINKS = [
  {
    key: "privacy_policy",
    label: "Privacy Policy",
    link: "https://vucaware.com/privacy-policy/",
    description: [
      "Privacy Policy",
      "Terms",
      "Modern Slavery Act",
      "Impression",
    ],
  },
  {
    key: "terms_of_service",
    label: "Terms of Service",
    link: "https://vucaware.com/terms-of-service/",
    description: [
      "Cool stuff",
      "Random feature",
      "Team feature",
      "Developer stuff",
      "Another one",
    ],
  },
  {
    key: "shipping_and_delivery_policy",
    label: "Shipping and Delivery Policy",
    link: "https://vucaware.com/shipping-and-delivery-policy/",
    description: [
      "Privacy Policy",
      "Terms",
      "Modern Slavery Act",
      "Impression",
    ],
  },
  {
    key: "contact_us",
    label: "Contact Us",
    link: "/contact-us",
    description: [],
  },
  {
    key: "widget",
    label: "Widget",
    link: "/widget",
    description: [],
  },
];

// Create a public axios instance without auth interceptors
const publicAxiosInstance = axios.create({
  timeout: 80000,
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

/**
 * Parse footer links from API response
 * Handles both array response and object with footerLinks/data property
 */
const parseFooterLinks = (response) => {
  if (!response || !response.data) {
    return null;
  }

  const data = response.data;

  // If response is directly an array
  if (Array.isArray(data)) {
    return data.length > 0 ? data : null;
  }

  // If response has footerLinks property
  if (data.footerLinks && Array.isArray(data.footerLinks)) {
    return data.footerLinks.length > 0 ? data.footerLinks : null;
  }

  // If response has data.footerLinks
  if (data.data && data.data.footerLinks && Array.isArray(data.data.footerLinks)) {
    return data.data.footerLinks.length > 0 ? data.data.footerLinks : null;
  }

  // If response has data.data (nested data)
  if (data.data && Array.isArray(data.data)) {
    return data.data.length > 0 ? data.data : null;
  }

  return null;
};

/**
 * Check if the current footer links are the default ones
 */
const isDefaultFooterLinks = (footerLinks) => {
  if (!footerLinks || footerLinks.length !== DEFAULT_FOOTER_LINKS.length) {
    return false;
  }
  // Check if the first link key matches the default
  return footerLinks[0]?.key === DEFAULT_FOOTER_LINKS[0]?.key;
};

/**
 * Thunk to fetch app configuration
 * Fetches footer links from /get-app-configuration endpoint
 * Falls back to static links on any error
 */
export const fetchAppConfiguration = () => {
  return async (dispatch, getState) => {
    // Check if footerLinks are already populated
    const state = getState();
    const currentFooterLinks = state?.appConfigurationReducer?.footerLinks;

    // Skip if footerLinks already exist and are NOT the default links
    // (meaning they came from a successful API fetch or persist)
    // If they are default links, we should still try to fetch from API
    if (currentFooterLinks && currentFooterLinks.length > 0 && !isDefaultFooterLinks(currentFooterLinks)) {
      return;
    }

    try {
      const response = await publicAxiosInstance.get("/get-app-configuration");

      // Check if response is OK (200, 201, 204)
      const isOK = response && response.status && [200, 201, 204].includes(response.status);

      if (isOK) {
        const footerLinks = parseFooterLinks(response);

        // If we got valid footer links, use them; otherwise use fallback
        if (footerLinks && footerLinks.length > 0) {
          dispatch(setFooterLinks(footerLinks));
        } else {
          // Empty or invalid response, use fallback
          dispatch(setFooterLinks(DEFAULT_FOOTER_LINKS));
        }
      } else {
        // Non-OK status, use fallback
        dispatch(setFooterLinks(DEFAULT_FOOTER_LINKS));
      }
    } catch (error) {
      // Network error, non-OK status, or any other error - use fallback
      console.error("Failed to fetch app configuration:", error);
      dispatch(setFooterLinks(DEFAULT_FOOTER_LINKS));
    }
  };
};

